import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Marketplace from "@/pages/Marketplace";
import Login from "@/pages/Login";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import Drivers from "@/pages/Drivers";
import HobbyFarmers from "@/pages/HobbyFarmers";
import HobbyFarmerProfile from "@/pages/HobbyFarmerProfile";
import Wholesale from "@/pages/Wholesale";
import MarketingKit from "@/pages/MarketingKit";
import MembershipSuccess from "@/pages/MembershipSuccess";
import Faq from "@/pages/Faq";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelect from "@/pages/RoleSelect";
import Dashboard from "@/pages/Dashboard";
import AdminLogin from "@/pages/AdminLogin";
import Layout from "@/components/Layout";
import InstallPrompt from "@/components/InstallPrompt";
import useHeartbeat from "@/hooks/useHeartbeat";

function HeartbeatRunner() {
  useHeartbeat();
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-stone-500 font-sans">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <Navigate to="/role-select" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/drivers" element={<Drivers />} />
        <Route path="/hobby-farmers" element={<HobbyFarmers />} />
        <Route path="/hobby-farmers/:userId" element={<HobbyFarmerProfile />} />
        <Route path="/wholesale" element={<Wholesale />} />
        <Route path="/marketing-kit" element={<ProtectedRoute><MarketingKit /></ProtectedRoute>} />
        <Route path="/membership/success" element={<MembershipSuccess />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/role-select" element={<RoleSelect />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <HeartbeatRunner />
            <InstallPrompt />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
